import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { useController, useFormContext } from "react-hook-form";
import Button from "../../../../components/Buttons/Button";
import {
  ALERT_CHANGE_CUSTOM_AUDIENCES_CAMPAIGN_DESCRIPTION,
  ALERT_CHANGE_CUSTOM_AUDIENCES_CAMPAIGN_TITLE,
  ALERT_CHANGE_CUSTOM_AUDIENCES_GROUPS_DESCRIPTION,
  ALERT_CHANGE_CUSTOM_AUDIENCES_GROUPS_TITLE,
  BUTTON_ADD_RE_TARGETING,
  BUTTON_CANCEL,
  BUTTON_CONTINUE,
  MODAL_CUSTOM_AUDIENCES_TITLE,
  MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE,
  RETENTION_DAYS_PLACEHOLDER,
  SIZE_FOR_DISPLAY_PLACEHOLDER,
  SIZE_FOR_SEARCH_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import AddIcon from "@mui/icons-material/Add";
import { GOOGLE } from "../../../../utils/constants";
import ModalCustomAudiencesGoogle from "./ModalCustomAudiencesGoogle";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import AudienceCard from "../../../Form/AudienceCard";
import { numberCompactFormat } from "../../../../utils/numbers";
import CardContainer from "../../../Card/CardContainer";

const platform = GOOGLE;

const CustomAudiencesContainerGoogle = ({
  control,
  from = "",
  name,
  disabled = false,
  isExcludedAudiences = false,
  afterTitle,
}) => {
  const [modalOpenCustomAudiences, setModalOpenCustomAudiences] =
    useState(false);
  const { t } = useTranslationApp();
  const { getValues, setValue } = useFormContext();

  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
  });

  const customAudiences = value || [];
  const platformData = getValues(`platformsData.${platform}`) || {};

  const validateChangeCustomAudiences = async () => {
    if (from === "campaign") {
      const adsGroups = getValues("adsGroups") || [];
      const assetsGroups = getValues("assetsGroups") || [];

      //verify if any adGroup or assetGroup has a custom audience (in platform google)
      const hasCustomAudience =
        adsGroups.some(
          (adGroup) =>
            adGroup.platform === platform &&
            adGroup?.customAudiences?.length > 0
        ) ||
        assetsGroups.some(
          (assetGroup) =>
            assetGroup.platform === platform &&
            assetGroup?.customAudiences?.length > 0
        );

      if (hasCustomAudience) {
        const result = await ConfirmAlert({
          title: t(ALERT_CHANGE_CUSTOM_AUDIENCES_CAMPAIGN_TITLE),
          text: t(ALERT_CHANGE_CUSTOM_AUDIENCES_CAMPAIGN_DESCRIPTION),
          confirmButtonText: t(BUTTON_CONTINUE),
          cancelButtonText: t(BUTTON_CANCEL),
        });

        if (!result.isConfirmed) return false;
      }

      return true;
    }

    if (from === "group") {
      const customAudiencesCampaign = platformData.customAudiences || [];
      if (customAudiencesCampaign.length > 0) {
        const result = await ConfirmAlert({
          title: t(ALERT_CHANGE_CUSTOM_AUDIENCES_GROUPS_TITLE),
          text: t(ALERT_CHANGE_CUSTOM_AUDIENCES_GROUPS_DESCRIPTION),
        });

        if (!result.isConfirmed) return false;
      }

      return true;
    }

    return true;
  };

  const onCleanInOtherLevels = () => {
    if (from === "campaign") {
      const adsGroups = getValues("adsGroups") || [];
      const assetsGroups = getValues("assetsGroups") || [];

      adsGroups.forEach((adGroup, index) => {
        if (adGroup.platform !== platform) return;

        setValue(`adsGroups.[${index}].customAudiences`, []);
      });

      assetsGroups.forEach((assetGroup, index) => {
        if (assetGroup.platform !== platform) return;
        setValue(`assetsGroups.[${index}].customAudiences`, []);
      });
    }

    if (from === "group") {
      setValue(`platformsData.${platform}.customAudiences`, []);
    }
  };

  return (
    <>
      <Grid item xs={12} id={name}>
        <CardContainer
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {" "}
                {t(
                  isExcludedAudiences
                    ? MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE
                    : MODAL_CUSTOM_AUDIENCES_TITLE
                )}
              </Typography>
              {afterTitle && <Box sx={{ mb: 1 }}>{afterTitle}</Box>}
            </Box>
          }
        >
          <Grid container spacing={2}>
            {customAudiences.map((customAudience) => (
              <AudienceCard
                key={customAudience.id}
                title={customAudience.name}
                isValid={customAudience.isValid}
                disabled={disabled}
                onDelete={() =>
                  onChange(value.filter((c) => c.id !== customAudience.id))
                }
                sx={{ color: "white" }}
              >
                {customAudience.description && (
                  <Typography variant="body2">
                    {customAudience.description}
                  </Typography>
                )}
                {customAudience?.retentionDays && (
                  <Typography variant="body2">
                    {`${t(RETENTION_DAYS_PLACEHOLDER)}: ${customAudience.retentionDays}`}
                  </Typography>
                )}
                {customAudience?.sizeForDisplay && (
                  <Typography variant="body2">
                    {`${t(SIZE_FOR_DISPLAY_PLACEHOLDER)}: ${numberCompactFormat(customAudience.sizeForDisplay || 0)}`}
                  </Typography>
                )}
                {customAudience?.sizeForSearch && (
                  <Typography variant="body2">
                    {`${t(SIZE_FOR_SEARCH_PLACEHOLDER)}: ${numberCompactFormat(customAudience.sizeForSearch || 0)}`}
                  </Typography>
                )}
              </AudienceCard>
            ))}

            {!disabled && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Button
                    type="button"
                    color="secondary"
                    startIcon={<AddIcon sx={{ color: "#000 !important" }} />}
                    onClick={() => setModalOpenCustomAudiences(true)}
                    sx={{
                      p: 1,
                      width: "100%",
                    }}
                  >
                    {t(BUTTON_ADD_RE_TARGETING)}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContainer>
      </Grid>
      <Box
        sx={{
          mb: 1,
        }}
      >
        {modalOpenCustomAudiences && (
          <ModalCustomAudiencesGoogle
            modalOpen={modalOpenCustomAudiences}
            onCloseModal={() => setModalOpenCustomAudiences(false)}
            isExcludedAudiences={isExcludedAudiences}
            adAccountID={platformData.adAccountID}
            integrationID={platformData.integrationID}
            filteredCustomAudiences={customAudiences.map((c) => c.id)}
            preValidationCallbackSave={validateChangeCustomAudiences}
            onCallbackSave={(customAudiences) => {
              onChange(
                value ? [...value, ...customAudiences] : customAudiences
              );
              onCleanInOtherLevels();
              setModalOpenCustomAudiences(false);
            }}
          />
        )}
      </Box>
    </>
  );
};

export default CustomAudiencesContainerGoogle;
